import React, { useState } from 'react'
import Box from '@material-ui/core/Box'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import Tooltip from '@material-ui/core/Tooltip'
import { FieldArray, useFormikContext } from 'formik'
import map from 'lodash/map'
import lowerCase from 'lodash/lowerCase'
import upperFirst from 'lodash/upperFirst'
import get from 'lodash/get'

import { appColors } from '../../globalStyles'
import { R } from '@utils/currency'
import { CardContent } from '@components/layout/Card'
import { Accordion } from '@components/Accordion'
import { FastNumberField } from '@components/Form/Fields'
import {
  totalCurrentForBudgetCategory, totalRetirementForBudgetCategory
} from './totals'

export const Content = ({ budgetCategories, readOnly }) => {
  return (
    <CardContent>
      {map(budgetCategories, (budgetItems, budgetCategory) =>
        <CategoryAccordion
          key={budgetCategory}
          budgetItems={budgetItems}
          budgetCategory={budgetCategory}
          budgetCategories={budgetCategories}
          readOnly={readOnly}
        />
      )}
    </CardContent>
  )
}

const CategoryAccordion = ({ budgetItems, budgetCategory, budgetCategories, readOnly }) => {
  const [expanded, setExpanded] = useState(false)
  return (
    <Accordion
      setExpanded={setExpanded}
      summary={
        <CategoryHeading
          expanded={expanded}
          budgetCategory={budgetCategory}
          budgetCategories={budgetCategories}
        />}
      details={
        <CategoryItems
          budgetItems={budgetItems}
          readOnly={readOnly}
        />}
    />
  )
}

const CategoryHeading = ({ budgetCategory, expanded, budgetCategories }) => {
  const { values: { detailed_budget, use_current_expense_for_retirement } } = useFormikContext()
  return (
    <Box
      display='flex'
      width='100%'
    >
      <Box flex='1'>{expanded ? '-' : '+'}</Box>
      <Box flex='5'>{humanizeString(budgetCategory)}</Box>
      <Box flex={use_current_expense_for_retirement ? '6' : '3'} textAlign='right'>{totalCurrentForBudgetCategory(budgetCategory, budgetCategories, detailed_budget)}</Box>
      <Box display={use_current_expense_for_retirement ? 'none' : 'block'} flex={use_current_expense_for_retirement ? '0' : '3'} textAlign='right'>{totalRetirementForBudgetCategory(budgetCategory, budgetCategories, detailed_budget)}</Box>
    </Box>
  )
}

const CategoryItems = ({ budgetItems, readOnly }) => {
  const { values } = useFormikContext()
  const { use_current_expense_for_retirement } = values

  return (
    <Box
      width='100%'
    >
      {map(budgetItems, (budgetItem) =>
        <FieldArray name={`detailed_budget.${budgetItem}`} key={budgetItem}
          render={() =>
            <Box
              display='flex'
              width='100%'
              className='detailed-budget__item'
            >
              <Box flex='1' />
              <Box
                display='flex'
                alignItems='center'
                flex='7'
              >
                <p>{humanizeString(budgetItem)}</p>
              </Box>
              <Box flex={use_current_expense_for_retirement ? '0' : '2'} />
              <Box flex={use_current_expense_for_retirement ? '8' : '2'} textAlign='right'>
                <FastNumberField disabled={readOnly} className='app-form__budget' name={`detailed_budget.${budgetItem}[0]`} placeholder={0} />
              </Box>
              <CopyValueButton
                budgetItem={budgetItem}
                readOnly={readOnly}
              />
              <Box display={use_current_expense_for_retirement ? 'none' : 'block'} flex={use_current_expense_for_retirement ? '0' : '2'} textAlign='right'>
                <FastNumberField disabled={readOnly || use_current_expense_for_retirement} className='app-form__budget' name={`detailed_budget.${budgetItem}[1]`} placeholder={0} />
              </Box>
            </Box>
          }
        />
      )}
    </Box>
  )
}

const CopyValueButton = ({ budgetItem, readOnly }) => {
  const { values, setFieldValue } = useFormikContext()
  const { use_current_expense_for_retirement, detailed_budget } = values
  const copyValue = R(get(detailed_budget, `${budgetItem}[0]`)).value

  if (readOnly) {
    return (
      <FlexBoxWithoutCopyButton use_current_expense_for_retirement={use_current_expense_for_retirement} />
    )
  }

  return (
    <Box display={use_current_expense_for_retirement ? 'none' : 'flex'} justifyContent='center' alignItems='center' flex={use_current_expense_for_retirement ? '0' : '2'}>
      <Tooltip className='item__tooltip' title='Copy this expense from the current field to the retirement field. This will replace the retirement field value.' placement='top'>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          color={appColors.system}
          style={{ cursor: 'pointer' }}
          onClick={() => setFieldValue(`detailed_budget.${budgetItem}[1]`, copyValue, false)}
        >
          <ArrowForwardIcon />
          <small>Copy</small>
        </Box>
      </Tooltip>
    </Box>
  )
}

const humanizeString = (value) => upperFirst(lowerCase(value))

const FlexBoxWithoutCopyButton = ({ use_current_expense_for_retirement }) =>
  <Box
    display={use_current_expense_for_retirement ? 'none' : 'flex'}
    flex={use_current_expense_for_retirement ? '0' : '2'}
  />
